import React, { useState } from "react";
import {
  FiActivity,
  FiArrowRight,
  FiBarChart2,
  FiClock,
  FiMapPin,
  FiNavigation,
  FiShoppingCart,
  FiUserCheck,
} from "react-icons/fi";

import sfaMobileExperience from "../../assets/sfaMobileExperience.jpg";

import "./sfaAbout.css";
import SfaBookingModal from "./sfaBookingModal";

const challenges = [
  {
    id: "01",
    icon: <FiNavigation />,
    title: "Manual Beat & Route Planning",
    text: "Sales teams often depend on spreadsheets, calls, WhatsApp messages or manual coordination to plan daily beats and outlet visits. Changing priorities, missed outlets and inefficient routes can make it difficult to maintain consistent field coverage.",
    impact: "Unclear daily execution",
    accent: "orange",
  },
  {
    id: "02",
    icon: <FiMapPin />,
    title: "Limited Field Visibility",
    text: "Managers need visibility into where sales representatives are working, which outlets have been visited and how planned activities are progressing. Without connected field data, monitoring territory execution becomes difficult.",
    impact: "Limited management visibility",
    accent: "teal",
  },
  {
    id: "03",
    icon: <FiUserCheck />,
    title: "Scattered Outlet Activity",
    text: "Customer information, outlet details, previous visits and field interactions can become fragmented across different systems or manual records. Sales representatives may not have the complete customer context when they reach the outlet.",
    impact: "Disconnected customer context",
    accent: "cyan",
  },
  {
    id: "04",
    icon: <FiShoppingCart />,
    title: "Delayed Order Capture",
    text: "When sales orders are recorded manually or through disconnected processes, information can take longer to reach the sales and management teams. This creates gaps between customer discussions, order capture and business visibility.",
    impact: "Slower sales visibility",
    accent: "purple",
  },
  {
    id: "05",
    icon: <FiClock />,
    title: "Manual Activity Reporting",
    text: "Sales representatives and managers can spend valuable time preparing reports, consolidating visit details and updating field activity. This administrative effort takes attention away from customer engagement and sales execution.",
    impact: "More administrative work",
    accent: "coral",
  },
  {
    id: "06",
    icon: <FiBarChart2 />,
    title: "Missed Execution Opportunities",
    text: "Unplanned visits, productive outlets, missed coverage and changing market activity can remain difficult to identify when field execution is not connected. Without a complete picture, opportunities may not receive timely attention.",
    impact: "Opportunities go unnoticed",
    accent: "green",
  },
];

const SfaAbout = () => {
  const [showSfaDemo, setShowSfaDemo] = useState(false);

  return (
    <>
      <section className="sfa-about-section">
        <div
          className="sfa-about-bg-circle sfa-about-bg-circle-one"
          aria-hidden="true"
        ></div>
        <div
          className="sfa-about-bg-circle sfa-about-bg-circle-two"
          aria-hidden="true"
        ></div>
        <div className="container">
          <div className="sfa-about-intro-row row align-items-center">
            <div className="col-xl-7 col-lg-7 col-md-12">
              <div className="sfa-mobile-experience-image-wrapper">
                <div className="sfa-mobile-image-glow" aria-hidden="true"></div>

                <div className="sfa-mobile-image-frame">
                  <img
                    src={sfaMobileExperience}
                    alt="TechCognics Sales Force Automation mobile experience"
                    className="sfa-mobile-experience-image"
                  />
                </div>
              </div>
            </div>

            <div className="col-xl-5 col-lg-5 col-md-12">
              <div className="sfa-about-content">
                <div className="sfa-about-eyebrow">
                  <span></span>
                  SFA MOBILE EXPERIENCE
                </div>

                <h2 className="sfa-about-title">
                  Everything Your Sales Team Needs,
                  <span>Right in Their Hands.</span>
                </h2>

                <p className="sfa-about-description">
                  From attendance to outlet visits, order capture and live field
                  activity, TechCognics SFA gives your sales team a powerful
                  mobile experience to sell smarter, every day.
                </p>

                <p className="sfa-about-description">
                  Sales representatives can manage their daily activities,
                  follow assigned beats, visit outlets, capture orders and keep
                  field activity connected with management visibility.
                </p>

                {/* Highlight */}
                <div className="sfa-about-highlight">
                  <div className="sfa-about-highlight-icon">
                    <FiActivity />
                  </div>

                  <div>
                    <strong>Everything connected in one field workflow</strong>

                    <p>
                      From starting the day with attendance to completing outlet
                      visits and capturing orders, keep field execution
                      connected from start to finish.
                    </p>
                  </div>
                </div>

                {/* Small feature pills */}
                <div className="sfa-mobile-feature-list">
                  <div className="sfa-mobile-feature-item">
                    <span>
                      <FiNavigation />
                    </span>
                    <div>
                      <strong>Beat Planning</strong>
                      <small>Plan daily outlet visits</small>
                    </div>
                  </div>

                  <div className="sfa-mobile-feature-item">
                    <span>
                      <FiMapPin />
                    </span>
                    <div>
                      <strong>Field Tracking</strong>
                      <small>Stay connected to activity</small>
                    </div>
                  </div>

                  <div className="sfa-mobile-feature-item">
                    <span>
                      <FiShoppingCart />
                    </span>
                    <div>
                      <strong>Order Capture</strong>
                      <small>Capture orders from the field</small>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="sfa-about-challenges">
            <div className="sfa-about-challenges-heading">
              <div>
                <span className="sfa-about-mini-label">
                  WITHOUT CONNECTED SFA
                </span>

                <h3>
                  What Happens Without
                  <span>Field Visibility?</span>
                </h3>
              </div>

              <p>
                Disconnected field processes can create gaps in planning,
                execution, customer engagement and management visibility.
              </p>
            </div>

            <div className="sfa-about-challenge-grid">
              {challenges.map((challenge, index) => (
                <div
                  className="sfa-challenge-card"
                  key={challenge.id}
                  style={{
                    "--sfa-card-delay": `${index * 90}ms`,
                  }}
                >
                  {/* Background number */}
                  <span className="sfa-challenge-bg-number">
                    {challenge.id}
                  </span>

                  {/* Top row */}
                  <div className="sfa-challenge-card-top">
                    <div className="sfa-challenge-label">
                      <span className="sfa-challenge-dot"></span>
                      WITHOUT SFA
                    </div>

                    <span className="sfa-challenge-number">{challenge.id}</span>
                  </div>

                  {/* Problem icon */}
                  <div className="sfa-challenge-icon">{challenge.icon}</div>

                  {/* Content */}
                  <h4>{challenge.title}</h4>

                  <p>{challenge.text}</p>

                  {/* Impact */}
                  <div className="sfa-challenge-impact">
                    <span className="sfa-challenge-impact-label">
                      BUSINESS IMPACT
                    </span>

                    <span className="sfa-challenge-impact-text">
                      {challenge.impact}
                    </span>
                  </div>

                  {/* Bottom line */}
                  <div className="sfa-challenge-bottom-line">
                    <span></span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="sfa-about-bottom">
            <div className="sfa-about-bottom-line"></div>

            <div className="sfa-about-bottom-content">
              <div>
                <span>THE RESULT</span>

                <h3>
                  Plan better. Execute smarter. Stay connected to the field.
                </h3>
              </div>

              <button
                type="button"
                className="sfa-about-cta"
                onClick={() => setShowSfaDemo(true)}
              >
                Book a Free SFA Demo
                <span>
                  <FiArrowRight />
                </span>
              </button>
            </div>
          </div>
        </div>
      </section>

      <SfaBookingModal
        show={showSfaDemo}
        onClose={() => setShowSfaDemo(false)}
      />
    </>
  );
};

export default SfaAbout;
